import { Provider } from '@vgtv/api-client';
import type { ProviderAccessMap } from '@vgtv/svp-access/lib/access_mappings.js';

import { getUserId } from '../schibstedAccount.js';
import { hasAccess } from '../paywall.js';
import { access } from '../svp/access.js';
import type { AppConfig } from '../../config.js';

import type { ActorObject } from './pulse.js';

const subscriptionNameMappings = {
  [Provider.VGTV]: {
    plus: 'VG+',
    sport: 'VG+ Sport',
  },
  [Provider.AB]: {
    plus: 'plus',
  },
} as const;

const getSubscriptionName = <T extends Provider>(
  provider: T,
  access: keyof (typeof subscriptionNameMappings)[T]
) => {
  return subscriptionNameMappings[provider][access];
};

const mapAccessesToSubscription = <T extends Provider>(
  provider: T,
  accesses: ProviderAccessMap[T][]
): string | undefined => {
  switch (provider) {
    case Provider.AB:
      return hasAccess('plus', accesses as ProviderAccessMap[Provider.AB][])
        ? getSubscriptionName(provider as Provider.AB, 'plus')
        : 'No';
    case Provider.VGTV:
      if (hasAccess('sport', accesses as ProviderAccessMap[Provider.VGTV][])) {
        return getSubscriptionName(provider as Provider.VGTV, 'sport');
      }

      if (hasAccess('plus', accesses as ProviderAccessMap[Provider.VGTV][])) {
        return getSubscriptionName(provider as Provider.VGTV, 'plus');
      }

      return 'No';
  }
};

export const getActorObject = async (
  provider: Provider,
  actorRealm: AppConfig['pulse']['actorRealm']
): Promise<ActorObject> => {
  try {
    const [userId, accesses] = await Promise.all([
      getUserId(),
      access.getAccesses(),
    ]);

    if (userId) {
      return {
        id: userId,
        realm: actorRealm,
        subscriptionName: mapAccessesToSubscription(provider, accesses),
      };
    }
  } catch (err) {
    console.error(err);
  }

  // pulse CIS sync expects actor to return an object with id,
  // even if the user is logged out
  return { id: undefined };
};
